import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismMessage, PrismTable } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { t } from 'ember-intl';
import TdVariance from 'lumen/components/hotel/net-revenue/cost-drilldown-section/td-variance';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  COST_COLUMN_KEYS,
  type CostColumnKey,
  DEFAULT_COLUMN_ORDER,
} from 'lumen/utils/net-revenue/cost-columns';
import type {
  CostProfileData,
  CostProfileTotals,
  CostTypeComparison,
  CostTypeRow,
} from 'lumen/utils/net-revenue/types';

import styles from './cost-drilldown-section.module.css';

interface CostRowDisplay {
  delta: string;
  deltaColor: string;
  formatted: string;
}

/**
 * Formats a metric value. The optional `signDisplay` is forwarded to
 * `Intl.NumberFormat` so delta values can show a locale-correct +/− sign
 * while the plain value omits it.
 */
type MetricFormatter = (
  value: number,
  signDisplay?: Intl.NumberFormatOptions['signDisplay'],
) => string;

interface CostRow extends CustomEmberTableRow {
  adr: number | null;
  adrDisplay: CostRowDisplay;
  cost: number | null;
  costDisplay: CostRowDisplay;
  costPercent: number | null;
  costPercentDisplay: CostRowDisplay;
  hasComparison: boolean;
  name: string | null;
  nameFormatted: string;
  netAdr: number | null;
  netAdrDisplay: CostRowDisplay;
  netRevenue: number | null;
  netRevenueDisplay: CostRowDisplay;
  netRevenuePercent: number | null;
  netRevenuePercentDisplay: CostRowDisplay;
  roomNights: number | null;
  roomNightsDisplay: CostRowDisplay;
  roomRevenue: number | null;
  roomRevenueDisplay: CostRowDisplay;
}

interface HotelNetRevenueCostDrilldownSectionSignature {
  Args: {
    columnOrder?: CostColumnKey[];
    data: CostProfileData | undefined;
    hiddenColumns?: CostColumnKey[];
    isError?: boolean;
    isLoading?: boolean;
  };
}

export default class HotelNetRevenueCostDrilldownSection extends Component<HotelNetRevenueCostDrilldownSectionSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @service declare intl: Services['intl'];

  initialSorts = [{ valuePath: 'netRevenue', isAscending: false }];

  get columns(): CustomEmberTableColumn[] {
    const profileNameColumn: CustomEmberTableColumn = {
      name: this.intl.t('net_revenue.profile_name'),
      valuePath: 'name',
      displayValuePath: 'nameFormatted',
      thClassNames: 'text-200--bold',
      tdClassNames: 'net-revenue-td-name',
      width: 200,
      textAlign: 'left',
    };

    const configurableColumns: Record<CostColumnKey, CustomEmberTableColumn> = {
      roomNights: {
        name: this.intl.t('general.room_nights'),
        valuePath: 'roomNights',
        displayValuePath: 'roomNightsDisplay.formatted',
        tdComponent: TdVariance,
        displayKey: 'roomNightsDisplay',
        tdClassNames: 'net-revenue-td-phantom-padding',
        width: 120,
        textAlign: 'right',
      },
      adr: {
        name: this.intl.t('general.adr'),
        valuePath: 'adr',
        displayValuePath: 'adrDisplay.formatted',
        tdComponent: TdVariance,
        displayKey: 'adrDisplay',
        tdClassNames: 'net-revenue-td-phantom-padding',
        width: 120,
        textAlign: 'right',
      },
      netAdr: {
        name: this.intl.t('net_revenue.net_adr'),
        valuePath: 'netAdr',
        displayValuePath: 'netAdrDisplay.formatted',
        tdComponent: TdVariance,
        displayKey: 'netAdrDisplay',
        tdClassNames: 'net-revenue-td-phantom-padding',
        width: 120,
        textAlign: 'right',
      },
      roomRevenue: {
        name: this.intl.t('net_revenue.room_revenue'),
        valuePath: 'roomRevenue',
        displayValuePath: 'roomRevenueDisplay.formatted',
        tdComponent: TdVariance,
        displayKey: 'roomRevenueDisplay',
        tdClassNames: 'net-revenue-td-phantom-padding',
        width: 140,
        textAlign: 'right',
      },
      netRevenue: {
        name: this.intl.t('net_revenue.net_revenue'),
        valuePath: 'netRevenue',
        displayValuePath: 'netRevenueDisplay.formatted',
        tdComponent: TdVariance,
        displayKey: 'netRevenueDisplay',
        tdClassNames: 'net-revenue-td-phantom-padding',
        width: 140,
        textAlign: 'right',
      },
      netRevenuePercent: {
        name: this.intl.t('net_revenue.net_revenue_percent'),
        valuePath: 'netRevenuePercent',
        displayValuePath: 'netRevenuePercentDisplay.formatted',
        tdComponent: TdVariance,
        displayKey: 'netRevenuePercentDisplay',
        tdClassNames: 'net-revenue-td-phantom-padding',
        width: 130,
        textAlign: 'right',
      },
      cost: {
        name: this.intl.t('net_revenue.cost'),
        valuePath: 'cost',
        displayValuePath: 'costDisplay.formatted',
        tdComponent: TdVariance,
        displayKey: 'costDisplay',
        tdClassNames: 'net-revenue-td-phantom-padding',
        width: 120,
        textAlign: 'right',
      },
      costPercent: {
        name: this.intl.t('net_revenue.cost_percent'),
        valuePath: 'costPercent',
        displayValuePath: 'costPercentDisplay.formatted',
        tdComponent: TdVariance,
        displayKey: 'costPercentDisplay',
        tdClassNames: 'net-revenue-td-phantom-padding',
        width: 100,
        textAlign: 'right',
      },
    };

    const order = this.args.columnOrder ?? DEFAULT_COLUMN_ORDER;
    const hidden = new Set(this.args.hiddenColumns ?? []);
    const visibleColumns = order
      .filter((key) => !hidden.has(key))
      .map((key) => configurableColumns[key]);

    return [profileNameColumn, ...visibleColumns];
  }

  get currencyCode(): string {
    return this.hotelAndMarketService.hotel.currencyCode;
  }

  get footerRows(): CostRow[] {
    const { data } = this.args;
    if (!data) {
      return [];
    }
    return [this.formatTotalsRow(data.totals)];
  }

  get hasComparison(): boolean {
    return this.args.data?.comparisonLabel != null;
  }

  get isEmpty(): boolean {
    return !this.args.isLoading && !this.args.data?.rows.length;
  }

  get rows(): CostRow[] {
    if (this.args.isError) {
      return [];
    }

    const { data } = this.args;
    const result = (data?.rows ?? []).map((row) => this.formatRow(row));

    const remaining = data?.remainingReservations;
    // Hidden once every reservation is attributed — i.e. no bucket, or a
    // zero-night one.
    if (remaining && remaining.roomNights > 0) {
      const blank: CostRowDisplay = {
        formatted: '',
        delta: '',
        deltaColor: '',
      };
      // Only room nights and room revenue are known for unattributed bookings.
      // Net ADR, Net revenue, Net revenue %, Cost and Cost % stay blank because
      // cost attribution is unknown, and no comparison is shown for this row.
      result.push({
        name: null,
        nameFormatted: this.intl.t('net_revenue.remaining_reservations'),
        hasComparison: false,
        roomNights: null,
        roomNightsDisplay: {
          ...blank,
          formatted: this.formatInteger(remaining.roomNights),
        },
        adr: null,
        adrDisplay: blank,
        netAdr: null,
        netAdrDisplay: blank,
        roomRevenue: null,
        roomRevenueDisplay: {
          ...blank,
          formatted: this.formatCurrency(remaining.roomRevenue),
        },
        netRevenue: null,
        netRevenueDisplay: blank,
        netRevenuePercent: null,
        netRevenuePercentDisplay: blank,
        cost: null,
        costDisplay: blank,
        costPercent: null,
        costPercentDisplay: blank,
      });
    }

    return result;
  }

  /**
   * Build the variance string shown beneath a metric. Returns the signed,
   * locale-formatted absolute delta (`actual - comparison`); for non-percent
   * columns it appends the relative change in parentheses, e.g.
   * `"+€1,234 (+15%)"`, `"−€500 (−8%)"`, `"+240 (+12%)"`. Percent columns
   * return the absolute part only (`"+1.2%"`), since the relative change would
   * just repeat the same number. A zero delta drops the sign (`"€0 (0%)"`),
   * and when `comparison` is `0` the relative term is `0%` (no meaningful
   * baseline). Only called when comparison data exists.
   */
  private buildDelta(
    formatter: MetricFormatter,
    actual: number,
    comparison: number,
    isPercentColumn: boolean,
  ): string {
    const delta = actual - comparison;
    // `signDisplay: 'exceptZero'` emits the locale-correct +/− sign from a
    // single source — avoids mixing an ASCII '+' with the Unicode minus that
    // `formatNumber` produces for negatives.
    const absStr = formatter(delta, 'exceptZero');
    // Percent columns already display a percent; the relative-change
    // parenthetical would just repeat the same number.
    if (isPercentColumn) {
      return absStr;
    }
    const relRatio = comparison !== 0 ? delta / comparison : 0;
    const relStr = `(${this.intl.formatNumber(relRatio, {
      style: 'percent',
      maximumFractionDigits: 0,
      signDisplay: 'exceptZero',
    })})`;
    return `${absStr} ${relStr}`;
  }

  private buildMetric(
    source: CostTypeRow,
    comparison: CostTypeComparison | null,
    key: CostColumnKey,
    formatMetric: MetricFormatter,
    options: { invertDeltaColors?: boolean; isPercentColumn?: boolean } = {},
  ): { display: CostRowDisplay; value: number } {
    const invertDeltaColors = options.invertDeltaColors ?? false;
    const isPercentColumn = options.isPercentColumn ?? false;
    const actualValue = source[key];
    const hasComparison = this.hasComparison;
    const missingText = this.intl.t('general.missing');

    let deltaText = '';
    if (hasComparison) {
      deltaText = comparison
        ? this.buildDelta(
            formatMetric,
            actualValue,
            comparison[key],
            isPercentColumn,
          )
        : missingText;
    }

    // Percent columns display with 1 decimal, so deltas < 0.05% render as
    // "+0%" / "-0%" and should be neutral, not coloured.
    const neutralThreshold = isPercentColumn ? 0.0005 : 0;

    const deltaColor =
      hasComparison && comparison
        ? this.deltaColor(
            actualValue,
            comparison[key],
            invertDeltaColors,
            neutralThreshold,
          )
        : '';

    return {
      value: actualValue,
      display: {
        formatted: formatMetric(actualValue),
        delta: deltaText,
        deltaColor,
      },
    };
  }

  private deltaColor(
    actual: number,
    comparison: number,
    invertColors: boolean,
    neutralThreshold: number,
  ): string {
    const delta = actual - comparison;
    if (Math.abs(delta) <= neutralThreshold) {
      return 'text-neutral-default';
    }
    const isPositive = delta > 0;
    return isPositive !== invertColors
      ? 'text-success-default'
      : 'text-critical-default';
  }

  private formatCurrency(
    n: number,
    maxFractionDigits = 0,
    signDisplay?: Intl.NumberFormatOptions['signDisplay'],
  ): string {
    return this.intl.formatNumber(n, {
      style: 'currency',
      currency: this.currencyCode,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: maxFractionDigits,
      signDisplay,
    });
  }

  private formatInteger(
    n: number,
    signDisplay?: Intl.NumberFormatOptions['signDisplay'],
  ): string {
    return this.intl.formatNumber(n, {
      maximumFractionDigits: 0,
      signDisplay,
    });
  }

  private formatPercent(
    n: number,
    signDisplay?: Intl.NumberFormatOptions['signDisplay'],
  ): string {
    return this.intl.formatNumber(n, {
      style: 'percent',
      maximumFractionDigits: 1,
      signDisplay,
    });
  }

  private formatRow(source: CostTypeRow): CostRow {
    const { comparison } = source;
    const formatAsCurrency: MetricFormatter = (value, signDisplay) =>
      this.formatCurrency(value, 0, signDisplay);
    const formatAsCurrencyWith2Decimals: MetricFormatter = (
      value,
      signDisplay,
    ) => this.formatCurrency(value, 2, signDisplay);
    const formatAsInteger: MetricFormatter = (value, signDisplay) =>
      this.formatInteger(value, signDisplay);
    const formatAsPercent: MetricFormatter = (value, signDisplay) =>
      this.formatPercent(value, signDisplay);

    const { value: roomNights, display: roomNightsDisplay } = this.buildMetric(
      source,
      comparison,
      COST_COLUMN_KEYS.ROOM_NIGHTS,
      formatAsInteger,
    );
    const { value: adr, display: adrDisplay } = this.buildMetric(
      source,
      comparison,
      COST_COLUMN_KEYS.ADR,
      formatAsCurrencyWith2Decimals,
    );
    const { value: netAdr, display: netAdrDisplay } = this.buildMetric(
      source,
      comparison,
      COST_COLUMN_KEYS.NET_ADR,
      formatAsCurrencyWith2Decimals,
    );
    const { value: roomRevenue, display: roomRevenueDisplay } =
      this.buildMetric(
        source,
        comparison,
        COST_COLUMN_KEYS.ROOM_REVENUE,
        formatAsCurrency,
      );
    const { value: netRevenue, display: netRevenueDisplay } = this.buildMetric(
      source,
      comparison,
      COST_COLUMN_KEYS.NET_REVENUE,
      formatAsCurrency,
    );
    const { value: netRevenuePercent, display: netRevenuePercentDisplay } =
      this.buildMetric(
        source,
        comparison,
        COST_COLUMN_KEYS.NET_REVENUE_PERCENT,
        formatAsPercent,
        { isPercentColumn: true },
      );
    // Cost columns invert delta colors — a smaller cost is "good" (green).
    const { value: cost, display: costDisplay } = this.buildMetric(
      source,
      comparison,
      COST_COLUMN_KEYS.COST,
      formatAsCurrency,
      { invertDeltaColors: true },
    );
    const { value: costPercent, display: costPercentDisplay } =
      this.buildMetric(
        source,
        comparison,
        COST_COLUMN_KEYS.COST_PERCENT,
        formatAsPercent,
        { invertDeltaColors: true, isPercentColumn: true },
      );

    return {
      name: source.name,
      nameFormatted: source.name,
      hasComparison: this.hasComparison,
      roomNights,
      roomNightsDisplay,
      adr,
      adrDisplay,
      netAdr,
      netAdrDisplay,
      roomRevenue,
      roomRevenueDisplay,
      netRevenue,
      netRevenueDisplay,
      netRevenuePercent,
      netRevenuePercentDisplay,
      cost,
      costDisplay,
      costPercent,
      costPercentDisplay,
    };
  }

  private formatTotalsRow(totals: CostProfileTotals): CostRow {
    const blankDisplay = (formatted: string): CostRowDisplay => ({
      formatted,
      delta: '',
      deltaColor: '',
    });

    return {
      className: 'text-200--bold',
      name: this.intl.t('general.total'),
      nameFormatted: this.intl.t('general.total'),
      hasComparison: false,
      roomNights: totals.roomNights,
      roomNightsDisplay: blankDisplay(this.formatInteger(totals.roomNights)),
      adr: null,
      adrDisplay: blankDisplay(this.intl.t('general.missing')),
      netAdr: totals.netAdr,
      netAdrDisplay: blankDisplay(this.formatCurrency(totals.netAdr, 2)),
      roomRevenue: totals.roomRevenue,
      roomRevenueDisplay: blankDisplay(this.formatCurrency(totals.roomRevenue)),
      netRevenue: totals.netRevenue,
      netRevenueDisplay: blankDisplay(this.formatCurrency(totals.netRevenue)),
      netRevenuePercent: totals.netRevenuePercent,
      netRevenuePercentDisplay: blankDisplay(
        this.formatPercent(totals.netRevenuePercent),
      ),
      cost: totals.cost,
      costDisplay: blankDisplay(this.formatCurrency(totals.cost)),
      costPercent: totals.costPercent,
      costPercentDisplay: blankDisplay(this.formatPercent(totals.costPercent)),
    };
  }

  <template>
    <div data-test-id="cost-drilldown-section">
      {{#if @isError}}
        <div
          class="rounded-200 bg-elevation-surface flex items-center justify-center overflow-hidden px-400 pt-800 pb-400 shadow-neutral-100"
          data-test-id="cost-drilldown-error"
        >
          <PrismMessage
            @illustration="100x100_critical"
            @title={{t "net_revenue.cost_drilldown_error"}}
          />
        </div>
      {{else if this.isEmpty}}
        <div
          class="rounded-200 bg-elevation-surface flex items-center justify-center overflow-hidden px-400 pt-800 pb-400 shadow-neutral-100"
          data-test-id="cost-drilldown-empty"
        >
          <PrismMessage
            @illustration="100x100_info"
            @title={{t "net_revenue.cost_drilldown_empty"}}
          />
        </div>
      {{else}}
        <PrismTable
          @columns={{this.columns}}
          @enableAutoSortsUpdate={{true}}
          @footerRows={{this.footerRows}}
          @initialSorts={{this.initialSorts}}
          @isLoading={{@isLoading}}
          @rows={{this.rows}}
          @sortEmptyLast={{true}}
          @widthConstraint="gte-container"
          class={{styles.table}}
        />
      {{/if}}
    </div>
  </template>
}
